/**
 * Date utility functions for the telehealth application
 * Provides standardized date formatting, validation, and calculation functions
 * 
 * @module dateUtils
 * @version 1.0.0
 * @author Telehealth Development Team
 */

/**
 * Formats a date object or date string into a specified format
 * 
 * @param {Date|string|null} date - The date to format
 * @param {string} [format='MM/DD/YYYY'] - The desired output format
 * @param {Object} [options={}] - Additional formatting options
 * @param {string} [options.locale='en-US'] - Locale for formatting
 * @param {string} [options.timeZone='UTC'] - Timezone for formatting
 * 
 * @returns {string} Formatted date string or 'Invalid Date' if input is invalid
 * 
 * @example
 * // Basic date formatting
 * const formatted = formatDate(new Date('2023-01-15'), 'MM/DD/YYYY');
 * console.log(formatted); // "01/15/2023"
 * 
 * @example
 * // With custom locale
 * const formatted = formatDate('2023-01-15', 'DD/MM/YYYY', { locale: 'en-GB' });
 * console.log(formatted); // "15/01/2023"
 * 
 * @example
 * // Handling invalid dates
 * const formatted = formatDate(null, 'MM/DD/YYYY');
 * console.log(formatted); // "Invalid Date"
 */
export const formatDate = (date, format = 'MM/DD/YYYY', options = {}) => {
  try {
    if (!date) {
      return 'Invalid Date';
    }

    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    const { locale = 'en-US', timeZone = 'UTC' } = options;

    // Format mapping for common patterns
    const formatMap = {
      'MM/DD/YYYY': { month: '2-digit', day: '2-digit', year: 'numeric' },
      'DD/MM/YYYY': { day: '2-digit', month: '2-digit', year: 'numeric' },
      'YYYY-MM-DD': { year: 'numeric', month: '2-digit', day: '2-digit' },
      'MMM DD, YYYY': { month: 'short', day: 'numeric', year: 'numeric' },
      'MMMM DD, YYYY': { month: 'long', day: 'numeric', year: 'numeric' },
    };

    const formatOptions = formatMap[format];
    if (!formatOptions) {
      throw new Error(`Unsupported format: ${format}`);
    }

    const formatted = dateObj.toLocaleDateString(locale, {
      ...formatOptions,
      timeZone,
    });

    // Handle YYYY-MM-DD format specifically
    if (format === 'YYYY-MM-DD') {
      const parts = formatted.split('/');
      return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    }

    return formatted;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Calculates age in years from a birth date
 * 
 * @param {Date|string} birthDate - The birth date
 * @param {Date|string} [referenceDate=new Date()] - Reference date for calculation
 * 
 * @returns {number} Age in years (can be negative if birth date is in the future)
 * 
 * @throws {Error} When birthDate is null, undefined, or invalid
 * 
 * @example
 * // Calculate current age
 * const age = calculateAge('1990-01-01');
 * console.log(age); // 33 (assuming current year is 2023)
 * 
 * @example
 * // Calculate age at specific date
 * const age = calculateAge('1990-01-01', '2020-01-01');
 * console.log(age); // 30
 * 
 * @example
 * // Handle future birth dates
 * const age = calculateAge('2025-01-01');
 * console.log(age); // -2 (assuming current year is 2023)
 */
export const calculateAge = (birthDate, referenceDate = new Date()) => {
  if (!birthDate) {
    throw new Error('Birth date is required');
  }

  const birth = new Date(birthDate);
  const reference = new Date(referenceDate);

  if (isNaN(birth.getTime())) {
    throw new Error('Invalid birth date format');
  }

  if (isNaN(reference.getTime())) {
    throw new Error('Invalid reference date format');
  }

  let age = reference.getFullYear() - birth.getFullYear();
  const monthDiff = reference.getMonth() - birth.getMonth();

  // Adjust age if birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && reference.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

/**
 * Calculates the duration between two dates/times
 * 
 * @param {Date|string} startDate - Start date/time
 * @param {Date|string} endDate - End date/time
 * @param {string} [unit='minutes'] - Unit for duration (minutes, hours, days)
 * 
 * @returns {number} Duration in the specified unit
 * 
 * @throws {Error} When dates are invalid or end date is before start date
 * 
 * @example
 * // Calculate appointment duration in minutes
 * const duration = getTimeDuration('2023-01-01T10:00', '2023-01-01T11:30');
 * console.log(duration); // 90
 * 
 * @example
 * // Calculate duration in hours
 * const duration = getTimeDuration('2023-01-01T09:00', '2023-01-01T17:00', 'hours');
 * console.log(duration); // 8
 */
export const getTimeDuration = (startDate, endDate, unit = 'minutes') => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    throw new Error('Invalid date format');
  }

  if (end < start) {
    throw new Error('End date must be after start date');
  }

  const diffMs = end - start;

  switch (unit) {
    case 'minutes':
      return Math.floor(diffMs / (1000 * 60));
    case 'hours':
      return Math.floor(diffMs / (1000 * 60 * 60));
    case 'days':
      return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

/**
 * Checks if a given date/time falls within business hours
 * 
 * @param {Date|string} date - Date/time to check
 * @param {Object} [businessHours] - Business hours configuration
 * @param {number} [businessHours.startHour=9] - Start hour (24-hour format)
 * @param {number} [businessHours.endHour=17] - End hour (24-hour format)
 * @param {number[]} [businessHours.workDays=[1,2,3,4,5]] - Work days (0=Sunday, 6=Saturday)
 * 
 * @returns {boolean} True if within business hours, false otherwise
 * 
 * @example
 * // Check if current time is business hours
 * const isOpen = isBusinessHours(new Date());
 * 
 * @example
 * // Check with custom business hours
 * const isOpen = isBusinessHours('2023-01-01T14:00', {
 *   startHour: 8,
 *   endHour: 18,
 *   workDays: [1, 2, 3, 4, 5, 6] // Include Saturday
 * });
 */
export const isBusinessHours = (date, businessHours = {}) => {
  const {
    startHour = 9,
    endHour = 17,
    workDays = [1, 2, 3, 4, 5] // Monday to Friday
  } = businessHours;

  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return false;
  }

  const dayOfWeek = dateObj.getDay();
  const hour = dateObj.getHours();

  // Check if it's a work day
  if (!workDays.includes(dayOfWeek)) {
    return false;
  }

  // Check if it's within business hours
  return hour >= startHour && hour < endHour;
};

/**
 * Validates if a date string matches a specific format
 * 
 * @param {string} dateString - Date string to validate
 * @param {string} [format='YYYY-MM-DD'] - Expected format
 * 
 * @returns {boolean} True if valid, false otherwise
 * 
 * @example
 * const isValid = isValidDateFormat('2023-01-01', 'YYYY-MM-DD');
 * console.log(isValid); // true
 * 
 * const isValid2 = isValidDateFormat('01/01/2023', 'YYYY-MM-DD');
 * console.log(isValid2); // false
 */
export const isValidDateFormat = (dateString, format = 'YYYY-MM-DD') => {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  const formatRegex = {
    'YYYY-MM-DD': /^\d{4}-\d{2}-\d{2}$/,
    'MM/DD/YYYY': /^\d{2}\/\d{2}\/\d{4}$/,
    'DD/MM/YYYY': /^\d{2}\/\d{2}\/\d{4}$/,
  };

  const regex = formatRegex[format];
  if (!regex) {
    return false;
  }

  if (!regex.test(dateString)) {
    return false;
  }

  // Additional validation - check if date is actually valid
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Gets the start and end of a time period (day, week, month, year)
 * 
 * @param {Date|string} date - Reference date
 * @param {string} period - Time period ('day', 'week', 'month', 'year')
 * 
 * @returns {Object} Object with start and end dates
 * @returns {Date} returns.start - Start of the period
 * @returns {Date} returns.end - End of the period
 * 
 * @example
 * const { start, end } = getDateRange('2023-01-15', 'month');
 * console.log(start); // 2023-01-01T00:00:00.000Z
 * console.log(end);   // 2023-01-31T23:59:59.999Z
 */
export const getDateRange = (date, period) => {
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date');
  }

  let start, end;

  switch (period) {
    case 'day':
      start = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
      end = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 23, 59, 59, 999);
      break;
      
    case 'week':
      const dayOfWeek = dateObj.getDay();
      start = new Date(dateObj);
      start.setDate(dateObj.getDate() - dayOfWeek);
      start.setHours(0, 0, 0, 0);
      
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      break;
      
    case 'month':
      start = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
      end = new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0, 23, 59, 59, 999);
      break;
      
    case 'year':
      start = new Date(dateObj.getFullYear(), 0, 1);
      end = new Date(dateObj.getFullYear(), 11, 31, 23, 59, 59, 999);
      break;
      
    default:
      throw new Error(`Unsupported period: ${period}`);
  }

  return { start, end };
};

// Export all functions as default for convenience
const dateUtils = {
  formatDate,
  calculateAge,
  getTimeDuration,
  isBusinessHours,
  isValidDateFormat,
  getDateRange,
};

export const formatTime12Hour = (timeString) => {
  if (!timeString) return '';
  try {
    // Split the time string into hours and minutes
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; // Convert to 12-hour format
    return `${hour12}:${minutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeString; // Return original string if formatting fails
  }
};


export default dateUtils;